import React, {
  useCallback,
  useRef,
  useMemo,
  memo,
  useState,
  useEffect,
} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  CheckboxInput,
  FormButton,
  PageHeader,
  ReadMode,
  SelectInput,
  Text,
  TextArea,
  Title,
} from '@cochlear-design-system/foundation';
import { getLabelsForFields } from '../../utils/labelHelper';
import { routes } from '../../config';
import providerService from '../../services/providerService';
import userService from '../../services/userService';
import serviceRequestService from '../../services/serviceRequestService';
import CaseCreationSuccessAlert from './components/CaseCreationSuccessAlert';
import CaseCreationErrorAlert from './components/CaseCreationErrorAlert';

// =====================
// Hooks & Utilities
// =====================

const useLabels = (rendering) =>
  useMemo(
    () => getLabelsForFields(rendering.fields),
    [rendering.fields],
  );

const useProviderDetails = () => {
  const provider = providerService.getCurrentProvider();
  const { firstName, lastName, username, professionalId } =
    userService.getUserDetails();
  return {
    provider,
    username,
    userFullName: `${firstName} ${lastName}`,
    professionalId,
  };
};

export const ServiceRequestProblemReason = Object.freeze({
  HarmInjurySafety: 'Harm, Injury or Safety Incident',
  BatteryDamage: 'Battery Damaged or Feels Hot',
  SoundIssue: 'Sound Issue',
  MoistureDamage: 'Moisture Damage',
  BrokenCrackedPart: 'Broken or Cracked Part',
  PowerBatteryLife: 'Power Issue or Reduced Battery Life',
  PairingConnection: 'Pairing or Connection Issue',
});

const PROBLEM_TYPES = Object.entries(ServiceRequestProblemReason);

// =====================
// Main Component
// =====================

const ReportLoanerIssue = ({ rendering, country, lang }) => {
  const labels = useLabels(rendering);
  const history = useHistory();
  const location = useLocation();
  const { selectedLoaner } = location.state || {};
  const { provider, username, userFullName, professionalId } =
    useProviderDetails();

  useEffect(() => {
    if (!selectedLoaner) {
      history.replace(
        `/${country}/${lang}/${routes.dpx.clinicEquipment}`,
      );
    }
  }, [selectedLoaner, country, lang, history]);
  const [error, setError] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [caseData, setCaseData] = useState(null);

  const loanerDetails = useMemo(
    () => [
      { key: 'deviceType', value: selectedLoaner?.product?.type },
      {
        key: 'productFamily',
        value: selectedLoaner?.product?.family,
      },
      {
        key: 'description',
        value: selectedLoaner?.product?.description,
      },
      {
        key: 'serialNumber',
        value: selectedLoaner?.product?.serialNumber,
      },
    ],
    [selectedLoaner],
  );

  const addressParts = useMemo(() => {
    const clinicName = provider.name || '';
    const {
      street,
      city,
      postcode,
      country: ctry,
    } = provider.address || {};
    return [clinicName, street, city, postcode, ctry].filter(Boolean);
  }, [provider]);

  const onCancelReportLoanerIssue = useCallback(() => {
    history.push(`/${country}/${lang}/${routes.dpx.clinicEquipment}`);
  }, [country, lang, history]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        problemReported: Yup.string().required(
          labels[
            'labels.reportLoanerIssue.validation.problemReported.required'
          ],
        ),
        additionalInformation: Yup.string()
          .trim()
          .required(
            labels[
              'labels.reportLoanerIssue.validation.additionalInformation.required'
            ],
          ),
        reportIssueConsent: Yup.boolean()
          .required(
            labels[
              'labels.reportLoanerIssue.validation.consent.required'
            ] || 'Consent is required',
          )
          .oneOf(
            [true],
            labels[
              'labels.reportLoanerIssue.validation.consent.required'
            ] || 'Consent is required',
          ),
      }),
    [labels],
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        patientservicerequest: {
          status: 'New',
          provider: {
            cochlearId: provider.id,
          },
          professional: {
            cochlearId: professionalId,
          },
          asset: {
            id: selectedLoaner?.id,
          },
          problemReason: values.problemReported,
          problemDescription: values.additionalInformation,
          shippingContact: {
            provider: {
              cochlearId: provider.id,
            },
          },
          shippingAddress: {
            id: provider.address?.id,
          },
          metadata: {
            channel: 'mCP - Issue with Loaner/Exchange device',
            modifiedBy: username,
          },
        },
      };

      const response =
        await serviceRequestService.createServiceRequest(payload);

      if (!response?.data?.caseId) {
        throw new Error(
          'Invalid response: caseId is missing from the service request response.',
        );
      }

      setCaseData({ data: response?.data });
      setFormSubmitted(true);
    } catch (err) {
      setError(err);
      setFormSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      problemReported: '',
      additionalInformation: '',
      reportIssueConsent: false,
    },
    validationSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  const formikRef = useRef(formik);
  formikRef.current = formik;

  const handleProblemReportedChange = useCallback((_name, value) => {
    const f = formikRef.current;
    f.setFieldValue('problemReported', value);
    f.setFieldTouched('problemReported', true, false);
    if (f.errors?.problemReported) {
      f.setFieldError('problemReported', undefined);
    }
  }, []);

  const isSubmitDisabled = !formik.isValid || formik.isSubmitting;

  const showForm = !(formSubmitted && (caseData || error));

  if (!selectedLoaner) {
    return null;
  }

  return (
    <div className="ccl__container">
      <div className="ccl__main-clinic-equipment ccl__report-loaner-issue">
        <Button
          text={labels['labels.common.back']}
          variant="tertiary"
          icon="chevron-left"
          onClick={onCancelReportLoanerIssue}
          className="back-button"
          iconPosition="leading"
          data-analytics="report_loaner_issue_back_to_clinic_equipment"
        />

        {formSubmitted && caseData && (
          <CaseCreationSuccessAlert
            caseNumber={caseData?.data?.caseId || ''}
            labels={labels}
          />
        )}
        {formSubmitted && error && (
          <CaseCreationErrorAlert labels={labels} />
        )}

        {showForm && (
          <>
            <PageHeader
              className="ccl__main-clinic-equipment__title"
              title={{
                content:
                  labels['labels.reportLoanerIssue.header.title'],
              }}
            />
            <form onSubmit={formik.handleSubmit}>
              <div className="ccl__dpx_report-loaner-issue-wrapper">
                <DeviceDetails
                  loanerDetails={loanerDetails}
                  labels={labels}
                />
                <ProblemDetails
                  formik={formik}
                  labels={labels}
                  onProblemChange={handleProblemReportedChange}
                />
                <ShipToClinic
                  addressParts={addressParts}
                  labels={labels}
                />
                <ProfessionalDetails
                  userFullName={userFullName}
                  username={username}
                  labels={labels}
                />
                <Consent formik={formik} labels={labels} />
              </div>
              <div className="ccl-e-form-button-bar mt-6">
                <div className="ccl-e-form-button-bar__save">
                  <FormButton
                    type="save"
                    text={labels['labels.common.submit']}
                    progress={formik.isSubmitting ? 2 : undefined}
                    disabled={isSubmitDisabled}
                    data-analytics="submit_report_loaner_issue"
                  />
                </div>
                <div className="ccl-e-form-button-bar__cancel">
                  <Button
                    text={labels['labels.common.cancel']}
                    variant="secondary"
                    onClick={onCancelReportLoanerIssue}
                    data-analytics="cancel_report_loaner_issue"
                  />
                </div>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// =====================
// Subcomponents
// =====================

const SubTitle = memo(({ label, labels }) => (
  <Title content={labels[label]} size="heading-3" tag="h3" />
));

const Section = memo(({ titleKey, labels, children }) => (
  <div className="ccl__dpx_report-loaner-issue-wrapper__section">
    {titleKey ? <SubTitle label={titleKey} labels={labels} /> : null}
    <section>{children}</section>
  </div>
));

const DeviceDetails = memo(({ loanerDetails, labels }) => (
  <Section
    titleKey="labels.reportLoanerIssue.deviceDetails"
    labels={labels}
  >
    {loanerDetails.map(({ key, value }) => (
      <ReadMode
        key={key}
        details={[value]}
        title={
          labels[`labels.reportLoanerIssue.deviceDetails.${key}`]
        }
      />
    ))}
  </Section>
));

const PROBLEM_WARNING_MAP = {
  [ServiceRequestProblemReason.BatteryDamage]:
    'labels.reportLoanerIssue.problemDetails.warning.batteryDamage',
  [ServiceRequestProblemReason.HarmInjurySafety]:
    'labels.reportLoanerIssue.problemDetails.warning.harmInjurySafety',
};

const ProblemDetails = memo(({ formik, labels, onProblemChange }) => {
  const problemList = useMemo(
    () =>
      PROBLEM_TYPES.map(([reasonKey, reasonValue]) => ({
        label:
          labels[
            `labels.reportLoanerIssue.problemTypes.${reasonKey}`
          ] || reasonValue,
        value: reasonValue,
      })),
    [labels],
  );

  const warningLabelKey =
    PROBLEM_WARNING_MAP[formik.values.problemReported];

  return (
    <Section
      titleKey="labels.reportLoanerIssue.problemDetails"
      labels={labels}
    >
      <div className="ccs-form-webReason-wrapper">
        <SelectInput
          dataList={problemList}
          onChange={onProblemChange}
          onBlur={formik.handleBlur}
          label={
            labels[
              'labels.reportLoanerIssue.problemDetails.problemReported'
            ]
          }
          name="problemReported"
          value={formik.values.problemReported}
          promptText={
            labels[
              'labels.reportLoanerIssue.problemDetails.problemReported.prompt'
            ]
          }
          error={
            !!formik.touched.problemReported &&
            !!formik.errors.problemReported
          }
          errorMsg={
            formik.touched.problemReported
              ? formik.errors.problemReported
              : ''
          }
        />
        {warningLabelKey && (
          <Alert variant="warning">
            <Text
              content={labels[warningLabelKey]}
              type="body-text"
            />
          </Alert>
        )}
      </div>
      <div className="ccs-form-description-wrapper">
        <TextArea
          isFluid
          label={
            labels[
              'labels.reportLoanerIssue.problemDetails.additionalInformation'
            ]
          }
          name="additionalInformation"
          stateManaged={false}
          value={formik.values.additionalInformation}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            !!formik.touched.additionalInformation &&
            !!formik.errors.additionalInformation
          }
          errorMsg={
            formik.touched.additionalInformation
              ? formik.errors.additionalInformation
              : ''
          }
        />
      </div>
    </Section>
  );
});

const ShipToClinic = memo(({ addressParts, labels }) => (
  <Section
    titleKey="labels.reportLoanerIssue.shipToClinic"
    labels={labels}
  >
    {addressParts.map((part) => (
      <Text key={part} content={part} type="body-text" />
    ))}
  </Section>
));

const ProfessionalDetails = memo(
  ({ userFullName, username, labels }) => (
    <Section
      titleKey="labels.reportLoanerIssue.professionalDetails"
      labels={labels}
    >
      <Text content={userFullName} type="body-text" />
      <Text content={username} type="body-text" />
    </Section>
  ),
);

const Consent = memo(({ formik, labels }) => (
  <Section titleKey={null} labels={labels}>
    <CheckboxInput
      label={
        <Text
          content={labels['labels.reportLoanerIssue.consent']}
          isHTML
        />
      }
      name="reportIssueConsent"
      checked={formik.values.reportIssueConsent}
      onChange={formik.handleChange}
      error={
        !!formik.touched.reportIssueConsent &&
        !!formik.errors.reportIssueConsent
      }
      errorMsg={
        formik.touched.reportIssueConsent
          ? formik.errors.reportIssueConsent
          : ''
      }
    />
  </Section>
));
export default ReportLoanerIssue;
